import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import apiClient from "../api-client";
import { shouldRetry, getRetryDelay } from "./retry";
import type {
  NotificationDto,
  NotificationsPage,
  NotificationCategory,
  NotificationPreferenceDto,
} from "./dtos/notification.dto";

// ============ QUERY KEYS ============

export const notificationKeys = {
  all: ["notifications"] as const,
  list: () => [...notificationKeys.all, "list"] as const,
  unreadCount: () => [...notificationKeys.all, "unread-count"] as const,
  preferences: () => [...notificationKeys.all, "preferences"] as const,
};

// ============ HELPERS ============

function updateNotificationInPages(
  pages: NotificationsPage[],
  updater: (notification: NotificationDto) => NotificationDto | null,
): NotificationsPage[] {
  return pages.map((page) => {
    const results: NotificationDto[] = [];
    for (const n of page.results) {
      const updated = updater(n);
      if (updated !== null) results.push(updated);
    }
    return { ...page, results };
  });
}

// ============ QUERIES ============

export function useNotifications(enabled = true) {
  return useInfiniteQuery({
    queryKey: notificationKeys.list(),
    queryFn: async ({
      pageParam,
    }: {
      pageParam: string | undefined;
    }): Promise<NotificationsPage> => {
      const response = await apiClient.get("/notifications", {
        params: { limit: 20, cursor: pageParam },
      });
      return response.data;
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pageInfo.nextCursor,
    retry: shouldRetry,
    retryDelay: getRetryDelay,
    enabled,
  });
}

export function useUnreadCount(enabled = true) {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async (): Promise<number> => {
      const response = await apiClient.get<{ count: number }>(
        "/notifications/unread-count",
      );
      return response.data.count;
    },
    retry: shouldRetry,
    retryDelay: getRetryDelay,
    enabled,
    refetchInterval: 45_000,
    refetchOnWindowFocus: true,
  });
}

// ============ MUTATIONS ============

export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string): Promise<void> => {
      await apiClient.patch(`/notifications/${notificationId}/read`);
    },
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.list() });
      await queryClient.cancelQueries({
        queryKey: notificationKeys.unreadCount(),
      });

      const previousList = queryClient.getQueryData<
        InfiniteData<NotificationsPage>
      >(notificationKeys.list());
      const previousCount = queryClient.getQueryData<number>(
        notificationKeys.unreadCount(),
      );

      let wasUnread = false;
      if (previousList) {
        queryClient.setQueryData<InfiniteData<NotificationsPage>>(
          notificationKeys.list(),
          {
            ...previousList,
            pages: updateNotificationInPages(previousList.pages, (n) => {
              if (n.id === notificationId && !n.isRead) {
                wasUnread = true;
                return { ...n, isRead: true };
              }
              return n;
            }),
          },
        );
      }

      if (wasUnread && previousCount !== undefined) {
        queryClient.setQueryData(
          notificationKeys.unreadCount(),
          Math.max(0, previousCount - 1),
        );
      }

      return { previousList, previousCount };
    },
    onError: (_err, _id, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(notificationKeys.list(), context.previousList);
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(
          notificationKeys.unreadCount(),
          context.previousCount,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
      queryClient.invalidateQueries({
        queryKey: notificationKeys.unreadCount(),
      });
    },
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      await apiClient.post("/notifications/read-all");
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.list() });
      await queryClient.cancelQueries({
        queryKey: notificationKeys.unreadCount(),
      });

      const previousList = queryClient.getQueryData<
        InfiniteData<NotificationsPage>
      >(notificationKeys.list());
      const previousCount = queryClient.getQueryData<number>(
        notificationKeys.unreadCount(),
      );

      if (previousList) {
        queryClient.setQueryData<InfiniteData<NotificationsPage>>(
          notificationKeys.list(),
          {
            ...previousList,
            pages: updateNotificationInPages(previousList.pages, (n) => ({
              ...n,
              isRead: true,
            })),
          },
        );
      }
      queryClient.setQueryData(notificationKeys.unreadCount(), 0);

      return { previousList, previousCount };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(notificationKeys.list(), context.previousList);
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(
          notificationKeys.unreadCount(),
          context.previousCount,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
      queryClient.invalidateQueries({
        queryKey: notificationKeys.unreadCount(),
      });
    },
  });
}

export function useClearAllNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      await apiClient.delete("/notifications");
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.list() });
      await queryClient.cancelQueries({
        queryKey: notificationKeys.unreadCount(),
      });

      const previousList = queryClient.getQueryData<
        InfiniteData<NotificationsPage>
      >(notificationKeys.list());
      const previousCount = queryClient.getQueryData<number>(
        notificationKeys.unreadCount(),
      );

      if (previousList) {
        queryClient.setQueryData<InfiniteData<NotificationsPage>>(
          notificationKeys.list(),
          {
            ...previousList,
            pages: previousList.pages.map((page) => ({
              ...page,
              results: [],
            })),
          },
        );
      }
      queryClient.setQueryData(notificationKeys.unreadCount(), 0);

      return { previousList, previousCount };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(notificationKeys.list(), context.previousList);
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(
          notificationKeys.unreadCount(),
          context.previousCount,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
      queryClient.invalidateQueries({
        queryKey: notificationKeys.unreadCount(),
      });
    },
  });
}

// ============ PREFERENCES ============

export function useNotificationPreferences(enabled = true) {
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: async (): Promise<NotificationPreferenceDto[]> => {
      const response = await apiClient.get<NotificationPreferenceDto[]>(
        "/notifications/preferences",
      );
      return response.data;
    },
    retry: shouldRetry,
    retryDelay: getRetryDelay,
    enabled,
  });
}

export function useUpdateNotificationPreference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      category,
      ...body
    }: {
      category: NotificationCategory;
      inApp?: boolean;
      email?: boolean;
    }): Promise<NotificationPreferenceDto> => {
      const response = await apiClient.put<NotificationPreferenceDto>(
        `/notifications/preferences/${category}`,
        body,
      );
      return response.data;
    },
    onMutate: async ({ category, ...body }) => {
      await queryClient.cancelQueries({
        queryKey: notificationKeys.preferences(),
      });

      const previous = queryClient.getQueryData<NotificationPreferenceDto[]>(
        notificationKeys.preferences(),
      );

      if (previous) {
        queryClient.setQueryData<NotificationPreferenceDto[]>(
          notificationKeys.preferences(),
          previous.map((p) =>
            p.category === category ? { ...p, ...body } : p,
          ),
        );
      }

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          notificationKeys.preferences(),
          context.previous,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: notificationKeys.preferences(),
      });
    },
  });
}
